/**
 * FirebaseStorageService - Manages file uploads and folder structure in Firebase Storage
 *
 * Folder Structure:
 * {companyName}/
 *   └── {clientName_DOB}/
 *       ├── forms/
 *       │   └── completed/
 *       ├── notes/
 *       │   ├── provider/
 *       │   │   ├── session-notes/
 *       │   │   └── clinical-assessments/
 *       │   └── client/
 *       │       └── homework/
 *       └── media/
 *           ├── session-audio/
 *           └── session-video/
 */

import { ref, uploadBytes, getDownloadURL, listAll, deleteObject, getMetadata } from 'firebase/storage';
import { storage } from '../../config/firebase';

export interface UploadFileOptions {
  companyName: string;
  clientName: string;
  dateOfBirth: string;
  category: 'forms' | 'notes' | 'media';
  subcategory?: string; // e.g., 'completed', 'provider/session-notes', 'session-audio'
  filename: string;
  file: Blob | Uint8Array | ArrayBuffer;
  contentType?: string;
}

export interface FileMetadata {
  name: string;
  fullPath: string;
  size: number;
  contentType: string;
  timeCreated: string;
  downloadURL?: string;
}

class FirebaseStorageService {
  private static instance: FirebaseStorageService;

  public static getInstance(): FirebaseStorageService {
    if (!FirebaseStorageService.instance) {
      FirebaseStorageService.instance = new FirebaseStorageService();
    }
    return FirebaseStorageService.instance;
  }

  /**
   * Create complete folder structure for a new client
   */
  public async createClientFolderStructure(
    companyName: string,
    clientName: string,
    dateOfBirth: string
  ): Promise<void> {
    // Firebase Storage doesn't require explicit folder creation
    // Folders are created automatically when files are uploaded
    // But we'll create placeholder files to establish the structure

    const FileNamingService = require('../FileNamingService').default;
    const namingService = FileNamingService.getInstance();
    const basePath = namingService.generateClientFolderPath(companyName, clientName, dateOfBirth);

    // Create placeholder .gitkeep files to establish folder structure
    const folders = [
      'forms/completed',
      'notes/provider/session-notes',
      'notes/provider/clinical-assessments',
      'notes/client/homework',
      'media/session-audio',
      'media/session-video'
    ];

    const placeholder = new Blob([''], { type: 'text/plain' });

    try {
      await Promise.all(
        folders.map(folder =>
          this.uploadFile({
            companyName,
            clientName,
            dateOfBirth,
            category: folder.split('/')[0] as 'forms' | 'notes' | 'media',
            subcategory: folder.split('/').slice(1).join('/'),
            filename: '.gitkeep',
            file: placeholder,
            contentType: 'text/plain'
          })
        )
      );

      console.log(`✅ Created folder structure for ${clientName} (${basePath})`);
    } catch (error) {
      console.error('❌ Error creating client folder structure:', error);
      throw error;
    }
  }

  /**
   * Upload a file to Firebase Storage
   */
  public async uploadFile(options: UploadFileOptions): Promise<{ downloadURL: string; fullPath: string }> {
    const {
      companyName,
      clientName,
      dateOfBirth,
      category,
      subcategory,
      filename,
      file,
      contentType = 'application/pdf'
    } = options;

    try {
      const FileNamingService = require('../FileNamingService').default;
      const namingService = FileNamingService.getInstance();
      const basePath = namingService.generateClientFolderPath(companyName, clientName, dateOfBirth);

      // Construct full path
      const subcategoryPath = subcategory ? `/${subcategory}` : '';
      const fullPath = `${basePath}/${category}${subcategoryPath}/${filename}`;

      // Create storage reference
      const storageRef = ref(storage, fullPath);

      // Upload file
      const metadata = {
        contentType,
        customMetadata: {
          clientName,
          dateOfBirth,
          companyName,
          uploadedAt: new Date().toISOString()
        }
      };

      await uploadBytes(storageRef, file, metadata);

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);

      console.log(`✅ Uploaded file: ${fullPath}`);
      return { downloadURL, fullPath };
    } catch (error) {
      console.error('❌ Error uploading file:', error);
      throw error;
    }
  }

  /**
   * Upload form PDF
   */
  public async uploadFormPDF(
    companyName: string,
    clientName: string,
    dateOfBirth: string,
    filename: string,
    pdfBlob: Blob
  ): Promise<{ downloadURL: string; fullPath: string }> {
    return this.uploadFile({
      companyName,
      clientName,
      dateOfBirth,
      category: 'forms',
      subcategory: 'completed',
      filename,
      file: pdfBlob,
      contentType: 'application/pdf'
    });
  }

  /**
   * Get download URL for existing file
   */
  public async getFileURL(fullPath: string): Promise<string> {
    try {
      const storageRef = ref(storage, fullPath);
      return await getDownloadURL(storageRef);
    } catch (error) {
      console.error('❌ Error getting file URL:', error);
      throw error;
    }
  }

  /**
   * List all files in a directory
   */
  public async listFiles(
    companyName: string,
    clientName: string,
    dateOfBirth: string,
    category: 'forms' | 'notes' | 'media',
    subcategory?: string
  ): Promise<FileMetadata[]> {
    try {
      const FileNamingService = require('../FileNamingService').default;
      const namingService = FileNamingService.getInstance();
      const basePath = namingService.generateClientFolderPath(companyName, clientName, dateOfBirth);

      const subcategoryPath = subcategory ? `/${subcategory}` : '';
      const fullPath = `${basePath}/${category}${subcategoryPath}`;

      const storageRef = ref(storage, fullPath);
      const result = await listAll(storageRef);

      // Get metadata and download URLs for all files
      const files = await Promise.all(
        result.items.map(async (itemRef) => {
          const metadata = await getMetadata(itemRef);
          let downloadURL: string | undefined;

          try {
            downloadURL = await getDownloadURL(itemRef);
          } catch (error) {
            console.warn('Could not get download URL for:', itemRef.fullPath);
          }

          return {
            name: itemRef.name,
            fullPath: itemRef.fullPath,
            size: metadata.size,
            contentType: metadata.contentType || 'unknown',
            timeCreated: metadata.timeCreated,
            downloadURL
          };
        })
      );

      return files.filter(file => file.name !== '.gitkeep'); // Exclude placeholder files
    } catch (error) {
      console.error('❌ Error listing files:', error);
      return [];
    }
  }

  /**
   * List all completed forms for a client
   */
  public async listClientForms(
    companyName: string,
    clientName: string,
    dateOfBirth: string
  ): Promise<FileMetadata[]> {
    return this.listFiles(companyName, clientName, dateOfBirth, 'forms', 'completed');
  }

  /**
   * Delete a file
   */
  public async deleteFile(fullPath: string): Promise<void> {
    try {
      const storageRef = ref(storage, fullPath);
      await deleteObject(storageRef);
      console.log(`✅ Deleted file: ${fullPath}`);
    } catch (error) {
      console.error('❌ Error deleting file:', error);
      throw error;
    }
  }

  /**
   * Check if file exists
   */
  public async fileExists(fullPath: string): Promise<boolean> {
    try {
      const storageRef = ref(storage, fullPath);
      await getDownloadURL(storageRef);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get storage path for a client
   */
  public getClientBasePath(companyName: string, clientName: string, dateOfBirth: string): string {
    const FileNamingService = require('../FileNamingService').default;
    const namingService = FileNamingService.getInstance();
    return namingService.generateClientFolderPath(companyName, clientName, dateOfBirth);
  }
}

export default FirebaseStorageService;
